import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { AppContext } from '../../../sporttia/all';

interface SidebarStateValue {
    /** Sidebar fijo visible (escritorio). */
    open: boolean;
    /** Sidebar temporal visible (móvil). */
    openMobile: boolean;
    isMobile: boolean;
    /** Abre o cierra el sidebar del dispositivo actual. */
    toggle: () => void;
    /** Cierra el sidebar móvil (al navegar, por ejemplo). */
    closeMobile: () => void;
}

const SidebarStateContext = createContext<SidebarStateValue | null>(null);

/**
 * Estado del sidebar. En escritorio se apoya en `cxt.drawerOpen` (que ya
 * usaban la AppBar y el contenido para calcular su ancho); en móvil el drawer
 * es temporal y vive aquí.
 */
export function SidebarStateProvider({
    children,
}: {
    children: React.ReactNode;
}): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [openMobile, setOpenMobile] = useState<boolean>(false);

    const toggle = useCallback((): void => {
        if (isMobile) {
            setOpenMobile((previous) => !previous);
            return;
        }
        cxt.setDrawerOpen(!cxt.drawerOpen);
    }, [cxt, isMobile]);

    const closeMobile = useCallback((): void => {
        setOpenMobile(false);
    }, []);

    // Atajo ⌘B / Ctrl+B para plegar y desplegar, igual que en
    // sporttia-competitions.
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'b' && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                toggle();
            }
        };

        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [toggle]);

    const value = useMemo<SidebarStateValue>(
        () => ({
            open: cxt.drawerOpen,
            openMobile,
            isMobile,
            toggle,
            closeMobile,
        }),
        [cxt.drawerOpen, openMobile, isMobile, toggle, closeMobile],
    );

    return (
        <SidebarStateContext.Provider value={value}>
            {children}
        </SidebarStateContext.Provider>
    );
}

export function useSidebarState(): SidebarStateValue {
    const value = useContext(SidebarStateContext);

    if (!value) {
        throw new Error(
            'useSidebarState must be used inside a SidebarStateProvider',
        );
    }

    return value;
}
